import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Send, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * OrderChatPanel — text-only chat between buyer and seller scoped to a single order.
 * Used in both Account.jsx (buyer side) and SellerDashboard.jsx (seller side).
 */
export default function OrderChatPanel({ order, currentUserId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${order.id}/chat`);
      setMessages(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat chat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [order.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  // Auto-refresh every 5s while open
  useEffect(() => {
    const t = setInterval(() => { load(); }, 5000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [order.id]);

  const send = async (e) => {
    e.preventDefault();
    const msg = draft.trim();
    if (!msg) return;
    setSending(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/chat`, { message: msg });
      setMessages((m) => [...m, data]);
      setDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal kirim");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="order-chat-modal">
      <div className="w-full max-w-lg border border-[#00F0FF]/40 bg-[#0a0a0a] flex flex-col" style={{ maxHeight: "90vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
            <div className="min-w-0">
              <h3 className="font-display font-bold text-white text-sm truncate">Chat Order #{order.id.slice(0, 8)}</h3>
              <p className="text-[10px] uppercase tracking-widest text-white/40 truncate">{order.items?.[0]?.title}</p>
            </div>
          </div>
          <button data-testid="close-chat-btn" onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[300px]" data-testid="chat-messages">
          {loading && messages.length === 0 ? (
            <p className="text-white/40 text-xs text-center py-4">Memuat...</p>
          ) : messages.length === 0 ? (
            <p className="text-white/40 text-xs text-center py-8">Belum ada pesan. Tulis pesan pertama untuk memulai percakapan.</p>
          ) : (
            messages.map((m) => {
              const isMe = m.sender_id === currentUserId;
              return (
                <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 text-sm ${isMe ? "bg-[#00F0FF] text-black" : "bg-white/10 text-white"}`}>
                    <p className="text-[10px] uppercase tracking-widest opacity-60 mb-1">{m.sender_name} · {m.sender_role}</p>
                    <p className="whitespace-pre-wrap break-words">{m.message}</p>
                    <p className="text-[9px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString("id-ID")}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
          <input
            data-testid="chat-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Tulis pesan..."
            maxLength={2000}
            className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2"
          />
          <button data-testid="chat-send-btn" type="submit" disabled={sending || !draft.trim()} className="px-4 bg-[#00F0FF] text-black hover:bg-[#33F0FF] disabled:opacity-50">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
